"use client"

import { ArrowRight } from "@phosphor-icons/react"
import Image from "next/image"
import Link from "next/link"
import { useTranslations } from "@/lib/intl/use-translations"
import type { ReviewerExpert } from "@/lib/reviewer-experts"
import { blobMediaUrl } from "@/lib/blob-url"
import { useLocalePath } from "@/lib/use-localized-navigation"

/**
 * The full-body MRI page's people (Eugene, 02.10): the home-v4 doctors block
 * ("Who creates your plan") turned to the scan. The radiologists lead. Donati
 * takes the statement, Fischer, Visser and Wiestler the cards; Battegay
 * (internal medicine), Komaromi (GP) and Erat (CMO) stay on the homepage. The
 * copy only rewords claims the live MRI page already makes (Eugene, 02.10):
 * `tests.mriCred3Body`, `mriCredStatNote`, `mriWalkStep3Body`, `expertsSub`.
 * Nothing that depends on the clinic, so no scanner or software names.
 */
const LEAD = "Donati"
const CARDS = ["Fischer", "Visser", "Wiestler"] as const

/**
 * Siemens Healthineers, then the radiologists' universities: Zurich for
 * Donati and Fischer, Munich for Wiestler. The mark is back without a claim
 * in the copy (Eugene, 02.10): the statement names no scanner or software,
 * since the partner clinics may not all use the same. One colour, as on the
 * homepage, so each file is a mask. `h` evens out the marks' optical size:
 * TUM's solid block reads heavier than the two wordmarks, so it runs
 * smallest.
 */
const LOGOS = [
  {
    src: "/images/logos/siemens.svg",
    alt: "Siemens Healthineers",
    ratio: 4,
    h: 24,
  },
  {
    src: "/images/logos/uzh.svg",
    alt: "University of Zurich",
    ratio: 154 / 53,
    h: 30,
  },
  {
    src: "/images/logos/tum.svg",
    alt: "Technical University of Munich",
    ratio: 408.16 / 212.47,
    h: 20,
  },
] as const

export function MriDoctors({ experts }: { experts: ReviewerExpert[] }) {
  const t = useTranslations("pricingV5")
  const lp = useLocalePath()
  const find = (surname: string) =>
    experts.find((e) => e.name.includes(surname))
  const lead = find(LEAD)
  const cards = CARDS.flatMap((surname) => {
    const doc = find(surname)
    return doc ? [{ ...doc, title: t(`mriDoctorsCaption${surname}`) }] : []
  })
  if (!lead && cards.length === 0) return null

  return (
    <section className="bg-background pt-16 pb-16 md:pt-[92px] md:pb-[115px]">
      <div className="mx-auto max-w-[1440px] px-4 md:px-10">
        <p className="type-label-lg text-neutral-500">
          {t("mriDoctorsEyebrow")}
        </p>
        <h2 className="type-display-md-emphasized mt-[11px] max-w-[640px] text-neutral-900">
          {t("mriDoctorsHeading")}
        </h2>

        {lead && (
          <figure className="mt-10 flex flex-col gap-6 md:mt-[60px] md:flex-row md:gap-10">
            <Image
              src={lead.image}
              alt={lead.name}
              width={108}
              height={108}
              className="size-[108px] shrink-0 rounded-[12px] object-cover"
            />
            <div className="max-w-[820px]">
              <p className="type-headline-md font-normal text-neutral-900">
                {t("mriDoctorsStatement")}
              </p>
              <figcaption className="mt-4">
                <p className="type-body-lg-emphasized text-neutral-950">
                  {lead.name}
                </p>
                <p className="type-body-md mt-1 text-neutral-500">
                  {t("mriDoctorsCaptionDonati")}
                </p>
              </figcaption>
            </div>
          </figure>
        )}

        {/* Three cards, so phones get one column of the desktop's
            horizontal card rather than a 2-up grid with an orphan. */}
        <ul className="mt-12 grid max-w-[1142px] grid-cols-1 gap-3 md:mt-[52px] md:ml-[136px] md:gap-[18px] lg:grid-cols-3">
          {cards.map((doc) => (
            <li
              key={doc.name}
              className="flex flex-row items-center gap-4 rounded-[20px] bg-white p-3 md:gap-[18px]"
            >
              <Image
                src={doc.image}
                alt={doc.name}
                width={144}
                height={144}
                sizes="72px"
                className="size-[72px] shrink-0 rounded-[12px] object-cover"
              />
              <div className="flex flex-col gap-1">
                <p className="type-body-lg-emphasized text-neutral-950">
                  {doc.name}
                </p>
                <p className="type-body-md text-neutral-500">{doc.title}</p>
              </div>
            </li>
          ))}
        </ul>

        {/* The marks a touch smaller and lighter than the homepage's
            (Eugene, 02.10), with the way to everyone else on the right:
            the About page's team, which lists the advisory board too. */}
        <div className="mt-8 flex max-w-[1142px] flex-col gap-6 md:mt-[32px] md:ml-[148px] md:flex-row md:items-center md:justify-between">
          <ul className="flex flex-wrap items-center gap-x-10 gap-y-6 opacity-60">
            {LOGOS.map((logo) => (
              <li key={logo.alt}>
                <span
                  role="img"
                  aria-label={logo.alt}
                  className="block [mask-size:contain] [mask-position:left_center] [mask-repeat:no-repeat]"
                  style={{
                    height: logo.h,
                    aspectRatio: logo.ratio,
                    // Oxford's blue, the homepage marks' one colour (`.hv3-mark`).
                    backgroundColor: "#002147", // ds-color-ignore
                    WebkitMaskImage: `url(${blobMediaUrl(logo.src)})`,
                    maskImage: `url(${blobMediaUrl(logo.src)})`,
                  }}
                />
              </li>
            ))}
          </ul>
          <Link
            href={`${lp("/about")}#team`}
            className="type-body-md inline-flex items-center gap-1.5 font-medium text-neutral-900 underline-offset-4 hover:underline"
          >
            {t("mriDoctorsTeamLink")}
            <ArrowRight aria-hidden size={16} />
          </Link>
        </div>
      </div>
    </section>
  )
}
